import { useDemoGuard, useServiceHistories } from '@hooks';
import { useStore } from '@stores';
import { Alert, Checkbox, Space, Typography } from 'antd';
import { observer } from 'mobx-react';
import React, { useCallback, useMemo, useState } from 'react';
import Presentation from './VehicleHistoryTable.presentation';

interface IProps {
  vin: string;
  registrationNumber?: string;
  customerId?: string;
  customerEmail?: string;
  customerFullName?: string;
  compact?: boolean;
}

export const VehicleHistoryTable: React.FC<IProps> = observer(({
  vin,
  registrationNumber,
  customerId: _customerId,
  customerEmail,
  customerFullName,
  compact = false,
}) => {
  const { serviceHistoryStore } = useStore();

  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSending, setEmailSending] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(['Umoto']);

  const historyHook = useServiceHistories(vin);

  const validateEmail = (val: string) => {
    if (!val) return null;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(val)) return 'Invalid email address';
    return null;
  };

  const { guard } = useDemoGuard();

  const handleSendEmailOpen = useMemo(
    () =>
      guard(() => {
        setEmail(customerEmail?.trim() || '');
        setEmailError(null);
        setEmailModalOpen(true);
      }),
    [customerEmail, guard]
  );

  const handleEmailCancel = useCallback(() => {
    setEmailModalOpen(false);
    setEmail('');
    setEmailError(null);
  }, []);

  const handleEmailChange = (val: string) => {
    setEmail(val);
    setEmailError(validateEmail(val));
  };

  const handleStatusChange = useCallback((statuses: string[]) => {
    setSelectedStatuses(statuses);
  }, []);

  const handleViewAll = useCallback(() => {
    if (registrationNumber) {
      window.open(`/vehicle-histories/${encodeURIComponent(registrationNumber)}`, '_blank', 'noopener,noreferrer');
    }
  }, [registrationNumber]);

  const handleEmailSend = useCallback(async () => {
    const error = validateEmail(email);
    setEmailError(error);
    if (error) return;
    if (selectedStatuses.length === 0) return;

    setEmailSending(true);
    try {
      await serviceHistoryStore.sendServiceHistoryEmail(
        vin,
        email || customerEmail,
        customerFullName,
        selectedStatuses,
      );
      setEmailModalOpen(false);
    } catch {
      // error handled by store
    } finally {
      setEmailSending(false);
    }
  }, [serviceHistoryStore, vin, email, customerEmail, customerFullName, selectedStatuses]);

  const emailExtraContent = useMemo(() => (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
          Include history types
        </Typography.Text>
        <Checkbox.Group value={selectedStatuses} onChange={(vals) => handleStatusChange(vals as string[])}>
          <Space direction="horizontal" size="middle">
            <Checkbox value="Umoto">Umoto</Checkbox>
            <Checkbox value="Imported">Imported</Checkbox>
            <Checkbox value="Unverified">Unverified</Checkbox>
          </Space>
        </Checkbox.Group>
        {selectedStatuses.length === 0 && (
          <Typography.Text type="danger" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
            Please select at least one history type
          </Typography.Text>
        )}
      </div>
      <Alert
        type="warning"
        showIcon
        message="Note on data reliability"
        description="Imported or unverified service history cannot be guaranteed as accurate. This information originates from external sources and may not reflect work performed by this garage."
        style={{ fontSize: 13 }}
      />
    </Space>
  ), [selectedStatuses, handleStatusChange]);

  return (
    <Presentation
      historyHook={historyHook}
      emailModalOpen={emailModalOpen}
      email={email}
      emailError={emailError}
      emailSending={emailSending}
      handleEmailCancel={handleEmailCancel}
      handleEmailChange={handleEmailChange}
      handleEmailSend={handleEmailSend}
      emailExtraContent={emailExtraContent}
      onViewAll={handleViewAll}
      onSendEmail={handleSendEmailOpen}
      selectedStatuses={selectedStatuses}
      compact={compact}
    />
  );
});
